import { UserIcon, PhoneIcon, UserGroupIcon, CheckIcon, ExclamationCircleIcon, InformationCircleIcon, CakeIcon, CalendarDaysIcon, ChevronDownIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import MealPreferenceCheckbox from './MealPreferenceCheckbox';
import { RSVPFormState, RSVPFormActions, EventData, MealIcons } from '../../types';

interface RSVPFormProps {
  formState: RSVPFormState;
  formActions: Omit<RSVPFormActions, 'handleSubmit'>;
  eventData: EventData;
  isRSVPOpen: () => boolean;
  format: (date: Date, format: string) => string;
  onSubmit: RSVPFormActions['handleSubmit'];
  mealIcons: MealIcons;
  onGenerateQR?: () => void; // Add new prop for QR generation
}

const RSVPForm = ({ formState, formActions, eventData, isRSVPOpen, format, onSubmit, mealIcons, onGenerateQR }: RSVPFormProps) => {
  const { rsvpStatus, familyContactName, mobileNumber, attendeesNumber, declinedCount, fieldErrors, isSubmitting, submitted, selectedMeals } = formState;

  const { setFamilyContactName, setMobileNumber, setAttendeesNumber, setDeclinedCount, setFieldErrors, handleMealSelect } = formActions;

  return (
    <div className="bg-white p-6 rounded-xl shadow-sm">
      <div className="mb-6">
        <h2 className="text-xl font-semibold flex items-center gap-2">
          <UserIcon className="w-6 h-6 text-purple-600" />
          RSVP Form
        </h2>
        <div className="mt-2 bg-blue-50 p-3 rounded-lg flex items-center gap-2 text-blue-800">
          <InformationCircleIcon className="w-5 h-5 flex-shrink-0" />
          <span className="text-sm">
            You can update your response until {format(
              new Date(eventData.startDate.getTime() - 86400000),
              'MMM dd, yyyy hh:mm a'
            )}
          </span>
        </div>
      </div>

      {isRSVPOpen() ? (
        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="flex items-center gap-2 text-yellow-800">
            <ExclamationCircleIcon className="w-5 h-5" />
            <span className="font-medium">
              RSVP submissions are closed as the event starts within 24 hours
            </span>
          </div>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-6">
          <div className="space-y-4">
            {/* Attendance Status */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Attendance Status *
              </label>
              <div className={`relative rounded-lg ${fieldErrors.rsvpStatus ? 'border-red-500' : 'border-gray-200'}`}>
                <select
                  value={rsvpStatus}
                  onChange={(e) => {
                    const status = e.target.value as 'confirmed' | 'declined' | '';
                    formActions.setRsvpStatus(status);
                    setFieldErrors((prev: Record<string, string>) => ({ ...prev, rsvpStatus: '' }));
                  }}
                  className="w-full pl-4 pr-10 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 appearance-none"
                >
                  <option value="">Select your response</option>
                  <option value="confirmed">✅ Confirmed - I will attend</option>
                  <option value="declined">❌ Declined - Can't attend</option>
                </select>
                <ChevronDownIcon className="w-5 h-5 absolute right-3 top-3.5 text-gray-400 pointer-events-none" />
              </div>
              {fieldErrors.rsvpStatus && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <ExclamationCircleIcon className="w-4 h-4" />
                  {fieldErrors.rsvpStatus}
                </p>
              )}
            </div>

            {/* Contact Person Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact Person Name *
              </label>
              <div className={`flex items-center gap-2 p-2 rounded-lg border ${fieldErrors.familyContactName ? 'border-red-500' : 'border-gray-200'
                } focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200`}>
                <UserIcon className="w-5 h-5 text-gray-400 ml-2" />
                <input
                  type="text"
                  placeholder="John Doe"
                  value={familyContactName}
                  onChange={(e) => {
                    setFamilyContactName(e.target.value);
                    setFieldErrors((prev: Record<string, string>) => ({ ...prev, familyContactName: '' }));
                  }}
                  className="flex-1 outline-none bg-transparent py-2 border-none focus:border-transparent focus:ring-0"
                />
              </div>
              {fieldErrors.familyContactName && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <ExclamationCircleIcon className="w-4 h-4" />
                  {fieldErrors.familyContactName}
                </p>
              )}
            </div>

            {/* Mobile Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Mobile Number *
              </label>
              <div className={`flex items-center gap-2 p-2 rounded-lg border ${fieldErrors.mobileNumber ? 'border-red-500' : 'border-gray-200'
                } focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200`}>
                <PhoneIcon className="w-5 h-5 text-gray-400 ml-2" />
                <input
                  type="tel"
                  placeholder="9876543210"
                  value={mobileNumber}
                  onChange={(e) => {
                    setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10));
                    setFieldErrors((prev: Record<string, string>) => ({ ...prev, mobileNumber: '' }));
                  }}
                  className="flex-1 outline-none bg-transparent py-2 border-none focus:border-transparent focus:ring-0"
                />
              </div>
              {fieldErrors.mobileNumber && (
                <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                  <ExclamationCircleIcon className="w-4 h-4" />
                  {fieldErrors.mobileNumber}
                </p>
              )}
            </div>

            {rsvpStatus === 'confirmed' && (
              <>
                {/* Number of People */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Number of People Attending *
                  </label>
                  <div className={`flex items-center gap-2 p-2 rounded-lg border ${fieldErrors.attendeesNumber ? 'border-red-500' : 'border-gray-200'
                    } focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200`}>
                    <UserGroupIcon className="w-5 h-5 text-gray-400 ml-2" />
                    <input
                      type="number"
                      value={attendeesNumber}
                      onChange={(e) => {
                        setAttendeesNumber(e.target.value);
                        setFieldErrors((prev: Record<string, string>) => ({ ...prev, attendeesNumber: '' }));
                      }}
                      className="flex-1 outline-none bg-transparent py-2 border-none focus:border-transparent focus:ring-0"
                    />
                  </div>
                  {fieldErrors.attendeesNumber && (
                    <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                      <ExclamationCircleIcon className="w-4 h-4" />
                      {fieldErrors.attendeesNumber}
                    </p>
                  )}
                </div>

                {/* Meal Preferences */}
                <div className="border-t pt-6">
                  <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <CakeIcon className="w-6 h-6 text-blue-500" />
                    Meal Preferences
                  </h3>
                  <div className="space-y-4">
                    {eventData.days.map((day, dayIndex) => (
                      <div key={dayIndex} className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <CalendarDaysIcon className="w-5 h-5 text-gray-600" />
                            <span className="font-medium text-gray-900">
                              {format(day.date, 'EEE, MMM dd')}
                            </span>
                          </div>
                        </div>
                        <div className="space-y-2">
                          {day.meals.map((meal, mealIndex) => (
                            <MealPreferenceCheckbox
                              key={mealIndex}
                              day={day}
                              meal={meal}
                              selectedMeals={selectedMeals}
                              handleMealSelect={handleMealSelect}
                              mealIcons={mealIcons}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {rsvpStatus === 'declined' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Number of People Not Attending *
                </label>
                <div className={`flex items-center gap-2 p-2 rounded-lg border ${fieldErrors.declinedCount ? 'border-red-500' : 'border-gray-200'
                  } focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-200`}>
                  <UserGroupIcon className="w-5 h-5 text-gray-400 ml-2" />
                  <input
                    type="number"
                    min="1"
                    value={declinedCount}
                    onChange={(e) => {
                      const value = Math.max(1, parseInt(e.target.value) || 1);
                      setDeclinedCount(value);
                      setFieldErrors((prev: Record<string, string>) => ({ ...prev, declinedCount: '' }));
                    }}
                    className="flex-1 outline-none bg-transparent py-2 border-none focus:border-transparent focus:ring-0"
                  />
                </div>
                {fieldErrors.declinedCount && (
                  <p className="text-red-500 text-sm mt-1 flex items-center gap-1">
                    <ExclamationCircleIcon className="w-4 h-4" />
                    {fieldErrors.declinedCount}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="mt-6">
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-75 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <ArrowPathIcon className="w-5 h-5 animate-spin" />
                  {submitted ? 'Updating...' : 'Submitting...'}
                </>
              ) : (
                <>
                  <CheckIcon className="w-5 h-5" />
                  {submitted && rsvpStatus !== 'declined' ? 'Update your event entry QR code' : rsvpStatus !== 'declined' ? 'Generate your event entry QR code' : "Update"}
                </>
              )}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};
export default RSVPForm